import React from 'react'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectFieldProps {
  label: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  hint?: string
  className?: string
}

export const SelectField: React.FC<SelectFieldProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder = 'Selecione...',
  required = false,
  disabled = false,
  error,
  hint,
  className = '',
}) => {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-foreground mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={cn(
            'w-full px-3 py-2 border rounded-md shadow-sm',
            'bg-card text-foreground',
            'focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent',
            'disabled:bg-surface disabled:text-muted-foreground disabled:cursor-not-allowed',
            'transition-colors duration-150',
            error
              ? 'border-red-500 ring-2 ring-red-200 dark:ring-red-800'
              : 'border-border',
          )}
        >
          <option value="" className="text-muted-foreground bg-card">{placeholder}</option>
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className="bg-card text-foreground"
            >
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {hint && !error && (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      )}

      {error && (
        <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  )
}
